import DOMPurify from "dompurify";
import { SquarePen, Trash2 } from "lucide-react";
import { useContext, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/app/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/app/components/ui/dialog";
import type { ResumeContextValue, WorkHistoryItem } from "@/app/types/resume";

import ResumeContext from "../../../context/ResumeContext";
import { dateToMY } from "../../../handlers/utilityFunctions";
import { resumeObjectToContextFormat } from "../../../handlers/valueTransformation";
import { StaticValue } from "../../../seed/constants";
import { usePatchResumeSection } from "../../../services/create-resume/queries";
import { RESUME_ACTIONS, RESUME_SECTIONS } from "../../../services/create-resume/resumeSections";

interface SummaryBoxProps {
	item: WorkHistoryItem;
	index: number | string;
}

const SummaryBox = ({ item, index }: SummaryBoxProps) => {
	const navigate = useNavigate();
	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;

	const details = item.details;

	function onSuccess(response: unknown) {
		resumeObjectToContextFormat(response, setResumeData);
	}

	function onError() {
		//TODO: handle error
		toast.error("Something went wrong");
	}
	const [cookies] = useCookies(["resume"]);

	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const resumeId = cookies[staticValues.localStorageResumeId];

	const patchSection = usePatchResumeSection();

	const deleteWorkHandler = () => {
		const itemToBeDeleted = item;

		const newActiveIndex = resumeData.activeWorkHistoryIndex === 0 ? 0 : resumeData.activeWorkHistoryIndex - 1;

		const filteredWorkDetails = resumeData.workHistory.filter((workItem) => workItem !== itemToBeDeleted);
		setResumeData((prevState) => {
			return {
				...prevState,
				workHistory: filteredWorkDetails,
				activeWorkHistoryIndex: newActiveIndex,
			};
		});
		setShowDeleteWarning(false);

		patchSection.mutate(
			{
				resumeId,
				section: RESUME_SECTIONS.EXPERIENCE,
				data: { referenceId: item.referenceId },
				action: RESUME_ACTIONS.DELETE,
			} as never,
			{ onSuccess, onError },
		);
	};

	function edit() {
		navigate("/resume/work-history", {
			state: {
				referenceId: index,
			},
		});
	}

	const [showDeleteWarning, setShowDeleteWarning] = useState(false);

	return (
		<>
			<div className="mb-5 rounded-lg border border-black/10 p-5 font-inter box">
				<div className="flex flex-col gap-3 md:flex-row md:justify-between">
					<div className="md:w-3/4">
						<div className="mb-3">
							<p className="m-0 font-semibold text-base text-black">
								{item.jobTitle}, {item.employer}
							</p>
							<p className="text-sm">
								{item.city}, {item.region}
								{item.country !== "undefined" && `, ${item.country}`} • {dateToMY(item.startDate)} -{" "}
								{item.endDate ? dateToMY(item.endDate) : "Present"}
							</p>
						</div>
						<div
							// `details` is user-authored rich HTML round-tripped through the
							// backend; sanitize before injecting so a crafted PATCH payload
							// cannot become stored XSS when another user opens this summary.
							dangerouslySetInnerHTML={{
								__html: DOMPurify.sanitize(details.length > 250 ? `${details.substring(0, 200)}...` : details),
							}}
						/>
					</div>
					{/* edit and delete buttons in the work summary */}
					<div className="flex items-start justify-end gap-2">
						<button
							type="button"
							onClick={edit}
							className="flex min-w-max cursor-pointer items-center gap-1.5 rounded-lg border border-gray-100 px-2 py-2.5 text-sm leading-none"
						>
							<SquarePen className="size-4 text-[#7a7a7a]" /> Edit
						</button>
						<button
							type="button"
							onClick={() => setShowDeleteWarning(true)}
							className="flex cursor-pointer items-center rounded-lg border border-gray-100 px-2 py-2.5 leading-none"
						>
							<Trash2 className="size-4 text-red-600" />
							<span className="sr-only">Delete</span>
						</button>
					</div>
				</div>
			</div>
			<Dialog open={showDeleteWarning} onOpenChange={setShowDeleteWarning}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>You are about to delete this item</DialogTitle>
						<DialogDescription>Are you sure you want to delete this item?</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button type="button" variant="secondary" onClick={() => setShowDeleteWarning(false)}>
							Cancel
						</Button>
						<Button type="button" onClick={deleteWorkHandler}>
							Delete
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default SummaryBox;
